var _templateObject;
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
function _toConsumableArray(r) { return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread(); }
function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _iterableToArray(r) { if ("undefined" != typeof Symbol && null != r[Symbol.iterator] || null != r["@@iterator"]) return Array.from(r); }
function _arrayWithoutHoles(r) { if (Array.isArray(r)) return _arrayLikeToArray(r); }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import { css, keyframes } from '@emotion/react';
import { getTokenName, euiCanAnimate, mathWithUnits } from '@elastic/eui-theme-common';
import { makeHighContrastColor, useEuiMemoizedStyles, isEuiThemeRefreshVariant } from '../../services';
import { highContrastModeStyles, logicalCSS } from '../functions';

/** Tentative usage; these exist only to be used as button directly when used within other components */
export var SEVERITY_COLORS = ['neutral', 'risk'];
export var BUTTON_COLORS = ['text', 'accent', 'accentSecondary', 'primary', 'success', 'warning', 'danger'];
export var EXTENDED_BUTTON_COLORS = [].concat(BUTTON_COLORS, SEVERITY_COLORS);
export var BUTTON_DISPLAYS = ['base', 'fill', 'empty'];
var getButtonVariantTokenValues = function getButtonVariantTokenValues(_ref, color, variant) {
  var euiTheme = _ref.euiTheme,
    highContrastMode = _ref.highContrastMode;
  var backgroundTokenBase = variant === 'base' ? 'background' : "background".concat(variant.charAt(0).toUpperCase() + variant.slice(1));
  var textTokenBase = variant === 'filled' ? 'textColorFilled' : 'textColor';
  var backgroundTokenName = getTokenName(backgroundTokenBase, color);
  var textTokenName = getTokenName(textTokenBase, color);
  var backgroundHoverTokenName = getTokenName(backgroundTokenBase, color, 'hover');
  var backgroundActiveTokenName = getTokenName(backgroundTokenBase, color, 'active');
  var highContrastForeground = ['warning', 'neutral', 'risk'].includes(color) ? euiTheme.colors.ink : color === 'disabled' ? euiTheme.components.buttons[textTokenName] : euiTheme.colors.textInverse;
  var foreground = variant === 'filled' ? highContrastMode ? highContrastForeground : euiTheme.components.buttons[textTokenName] : euiTheme.components.buttons[textTokenName];
  return {
    color: foreground,
    background: euiTheme.components.buttons[backgroundTokenName],
    backgroundHover: euiTheme.components.buttons[backgroundHoverTokenName],
    backgroundActive: euiTheme.components.buttons[backgroundActiveTokenName]
  };
};

/**
 * Creates the `base` version of button styles with proper text contrast.
 * @param euiThemeContext
 * @param color One of the named button colors or 'disabled'
 * @returns Style object `{ backgroundColor, color }`
 */
export var euiButtonColor = function euiButtonColor(euiThemeContext, color) {
  var buttonColors = getButtonVariantTokenValues(euiThemeContext, color, 'base');
  var foreground = buttonColors.color;
  var background = buttonColors.background;
  return _objectSpread({
    color: background === 'transparent' || color === 'disabled' ? foreground : makeHighContrastColor(foreground)(background),
    backgroundColor: background
  }, _highContrastBorder(euiThemeContext, foreground));
};

/**
 * Creates the `fill` version of buttons styles with proper text contrast.
 * @param euiThemeContext
 * @param color One of the named button colors or 'disabled'
 * @returns Style object `{ backgroundColor, color }`
 */
export var euiButtonFillColor = function euiButtonFillColor(euiThemeContext, color) {
  var buttonColors = getButtonVariantTokenValues(euiThemeContext, color, 'filled');
  var foreground = buttonColors.color;
  var background = buttonColors.background;
  return _objectSpread({
    color: foreground,
    backgroundColor: background
  }, _highContrastBorder(euiThemeContext, color === 'disabled' ? foreground : background // The border is necessary for Windows high contrast themes, which ignore background-color
  ));
};

/**
 * Creates the `empty` version of button styles using the text-variant and adding interactive styles.
 * @param euiThemeContext
 * @param color One of the named button colors or 'disabled'
 * @returns Style object `{ backgroundColor, color }` where `background` is typically used for interactive states
 */
export var euiButtonEmptyColor = function euiButtonEmptyColor(euiThemeContext, color) {
  var foreground;
  var background;
  switch (color) {
    case 'disabled':
      foreground = euiButtonColor(euiThemeContext, color).color;
      background = 'transparent';
      break;
    default:
      {
        var buttonColors = getButtonVariantTokenValues(euiThemeContext, color, 'empty');
        foreground = euiButtonColor(euiThemeContext, color).color;
        background = buttonColors.backgroundHover;
        break;
      }
  }
  return {
    color: foreground,
    backgroundColor: background
  };
};

/**
 * Given the button display type, returns the Emotion based color keys.
 * @param options Button display type
 * @returns An object of `_EuiExtendedButtonColor` keys including `disabled`
 */
export var useEuiButtonColorCSS = function useEuiButtonColorCSS() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _options$display = options.display,
    display = _options$display === void 0 ? 'base' : _options$display;
  var colorsDisplaysMap = useEuiMemoizedStyles(euiButtonDisplaysColors);
  return colorsDisplaysMap[display];
};
var euiButtonDisplaysColors = function euiButtonDisplaysColors(euiThemeContext) {
  var euiTheme = euiThemeContext.euiTheme;
  var isRefreshVariant = isEuiThemeRefreshVariant(euiThemeContext, 'buttonVariant');
  var COLORS = [].concat(_toConsumableArray(EXTENDED_BUTTON_COLORS), ['disabled']);
  var displaysColorsMap = {};
  BUTTON_DISPLAYS.forEach(function (display) {
    displaysColorsMap[display] = {};
    COLORS.forEach(function (color) {
      switch (display) {
        case 'base':
          {
            var buttonColors = getButtonVariantTokenValues(euiThemeContext, color, 'base');
            var borderStyle = color === 'text' && "\n              border: ".concat(euiTheme.border.width.thin, " solid ").concat(euiTheme.colors.borderBasePlain, ";\n            ");
            var refreshVariantStyles = isRefreshVariant && "\n            ".concat(_interactionStyles(euiThemeContext, buttonColors, 'overlay'), "\n            ").concat(borderStyle, "\n          ");
            displaysColorsMap[display][color] = /*#__PURE__*/css(euiButtonColor(euiThemeContext, color), " ", refreshVariantStyles, ";;label:displaysColorsMap-display-color;");
            break;
          }
        case 'fill':
          {
            var _buttonColors = getButtonVariantTokenValues(euiThemeContext, color, 'filled');
            var _refreshVariantStyles = isRefreshVariant && _interactionStyles(euiThemeContext, _buttonColors);
            displaysColorsMap[display][color] = /*#__PURE__*/css(euiButtonFillColor(euiThemeContext, color), "outline-color:", euiThemeContext.colorMode === 'DARK' && color === 'text' ? 'currentColor' : euiThemeContext.euiTheme.colors.fullShade, ";", _refreshVariantStyles, ";;label:displaysColorsMap-display-color;");
            break;
          }
        case 'empty':
          {
            var _buttonColors2 = getButtonVariantTokenValues(euiThemeContext, color, 'empty');
            var classicVariantStyles = !isRefreshVariant && "\n              &:focus,\n              &:active {\n                background-color: ".concat(euiButtonEmptyColor(euiThemeContext, color).backgroundColor, ";\n              }\n            ");
            var _refreshVariantStyles2 = isRefreshVariant && _interactionStyles(euiThemeContext, _buttonColors2, 'overlay');
            displaysColorsMap[display][color] = /*#__PURE__*/css("color:", euiButtonEmptyColor(euiThemeContext, color).color, ";", classicVariantStyles, " ", _refreshVariantStyles2, ";;label:displaysColorsMap-display-color;");
            break;
          }
      }

      // Tweak auto-generated Emotion label/className output
      var emotionOutput = displaysColorsMap[display][color];
      emotionOutput.styles = emotionOutput.styles.replace('label:displaysColorsMap-display-color;', "label:".concat(display, "-").concat(color, ";"));
    });
  });
  return displaysColorsMap;
};

/**
 * Creates the translate animation when button is in focus.
 * @returns string
 */
export var useEuiButtonFocusCSS = function useEuiButtonFocusCSS() {
  return useEuiMemoizedStyles(euiButtonFocusCSS);
};
var euiButtonFocusAnimation = keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  50% {\n    transform: translateY(1px);\n  }\n"])));
var euiButtonFocusCSS = function euiButtonFocusCSS(euiThemeContext) {
  var euiTheme = euiThemeContext.euiTheme;
  var isRefreshVariant = isEuiThemeRefreshVariant(euiThemeContext, 'buttonVariant');
  var focusCSS = isRefreshVariant ? /*#__PURE__*/css(";label:focusCSS;") : /*#__PURE__*/css(euiCanAnimate, "{transition:transform ", euiTheme.animation.normal, " ease-in-out,background-color ", euiTheme.animation.normal, " ease-in-out;&:hover:not(:disabled){transform:translateY(-1px);}&:focus{animation:", euiButtonFocusAnimation, " ", euiTheme.animation.normal, " ", euiTheme.animation.bounce, ";}&:active:not(:disabled){transform:translateY(1px);}};label:focusCSS;");
  // Remove the auto-generated label.
  // We could typically avoid a label by using a plain string `` instead of css``,
  // but we need css`` for keyframes`` to work for the focus animation
  focusCSS.styles = focusCSS.styles.replace('label:focusCSS;', '');
  return focusCSS;
};

/**
 * Map of `size` props to various sizings/scales
 * that should remain consistent across all buttons
 */
export var euiButtonSizeMap = function euiButtonSizeMap(euiThemeContext) {
  var euiTheme = euiThemeContext.euiTheme;
  var isRefreshVariant = isEuiThemeRefreshVariant(euiThemeContext, 'buttonVariant');
  return {
    xs: {
      minWidth: euiTheme.base * (isRefreshVariant ? 6 : 7),
      height: euiTheme.size.l,
      radius: euiTheme.border.radius.small,
      fontScale: 'xs'
    },
    s: {
      minWidth: euiTheme.base * (isRefreshVariant ? 6 : 7),
      height: euiTheme.size.xl,
      radius: euiTheme.border.radius.small,
      fontScale: 's'
    },
    m: {
      minWidth: euiTheme.base * 7,
      height: euiTheme.size.xxl,
      radius: isRefreshVariant ? euiTheme.border.radius.small : euiTheme.border.radius.medium,
      fontScale: 's'
    }
  };
};

/**
 * internal styles util for applying button background color on hover
 */
var _interactionStyles = function _interactionStyles(euiThemeContext, buttonColors) {
  var type = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'fill';
  var isRefreshVariant = isEuiThemeRefreshVariant(euiThemeContext, 'buttonVariant');
  if (!isRefreshVariant) return "";
  var baseStyles = function baseStyles() {
    // button hover is applied as pseudo element with a transparent background-color
    if (type === 'overlay') {
      return "\n        position: relative;\n        overflow: hidden;\n\n        &:hover:not(:disabled) {\n          &::before {\n            content: '';\n            position: absolute;\n            /* should stay under the content */\n            z-index: 0;\n            inset: 0;\n            background-color: ".concat(buttonColors.backgroundHover, ";\n            pointer-events: none;\n          }\n        }\n\n        &:active:not(:disabled) {\n          &::before {\n            ").concat(logicalCSS('width', '100%'), "\n            ").concat(logicalCSS('height', '100%'), "\n\n            content: '';\n            position: absolute;\n            inset: 0;\n            background-color: ").concat(buttonColors.backgroundActive, ";\n          }\n        }\n      ");
    }

    // button hover is applied as opaque color
    return "\n      &:hover:not(:disabled) {\n        background-color: ".concat(buttonColors.backgroundHover, ";\n      }\n\n      &:active:not(:disabled) {\n        background-color: ").concat(buttonColors.backgroundActive, ";\n      }\n    ");
  };
  return "    \n    ".concat(highContrastModeStyles(euiThemeContext, {
    none: baseStyles(),
    forced: "\n        position: relative;\n        overflow: hidden;\n\n        ".concat(highContrastHoverIndicatorStyles(euiThemeContext), "\n      ")
  }), "\n  ");
};

/**
 * creates a bottom border on hover/focus to ensure a visible change as forced mode removed background colors
 */
export var highContrastHoverIndicatorStyles = function highContrastHoverIndicatorStyles(_ref2) {
  var euiTheme = _ref2.euiTheme;
  return "\n  &:hover:not(:disabled) {\n    transition: none;\n\n    /* using pseudo border to be able to control the color */\n    &::after {\n      content: '';\n      position: absolute;\n      inset: ".concat(euiTheme.border.width.thin, ";\n      border: ").concat(euiTheme.border.width.thick, " solid var(--highContrastHoverIndicatorColor, ").concat(euiTheme.border.color, ");\n      border-radius: ").concat(mathWithUnits(euiTheme.border.radius.small, function (x) {
    return x / 2;
  }), ";\n      background-color: transparent;\n      pointer-events: none;\n    }\n  }\n");
};

/**
 * Internal util for high contrast button borders
 */
var _highContrastBorder = function _highContrastBorder(_ref3, color) {
  var highContrastMode = _ref3.highContrastMode,
    euiTheme = _ref3.euiTheme;
  return highContrastMode ? {
    border: "".concat(euiTheme.border.width.thin, " solid ").concat(color)
  } : {};
};